import { ArrowLeft2 } from "iconsax-react";
import type { Locale } from "@/shared/utils";
import type { DatePickerMode } from "../DatePicker";
import Button from "../Button";
import CalendarDays from "./CalendarDays";
import type { HighlightedRange } from "./CalendarDays";
import CalendarHeader from "./CalendarHeader";
import CalendarMonths from "./CalendarMonths";
import CalendarPeriods from "./CalendarPeriods";
import CalendarYears from "./CalendarYears";
import TimePicker from "./TimePicker";
import { CALENDAR_LOCALE } from "./locale";

interface RangeValue {
  start: Date | null;
  end: Date | null;
}

interface Props {
  currentMonth: Date;
  view: "day" | "month" | "year" | "period";
  next: () => void;
  prev: () => void;
  goYearMode: () => void;
  selectMonth: (month: number) => void;
  selectYear: (year: number) => void;
  selectDate: (date: Date) => void;
  value: RangeValue;
  hoverDate?: Date | null;
  onHover?: (d: Date | null) => void;
  locale: Locale;
  mode?: DatePickerMode;
  hasTime?: boolean;
  onChange?: (v: RangeValue | Date | null) => void;
  onClose?: () => void;
  minDate?: Date;
  maxDate?: Date;
  hasPeriodPicker?: boolean;
  goToPeriodView?: () => void;
  goToFreeRange?: () => void;
  selectPeriodMonth?: (month: number, year: number) => void;
  selectQuarter?: (quarter: number, year: number) => void;
  isInFreeRange?: boolean;
  highlightedRanges?: HighlightedRange[];
}

export default function Calendar({
  currentMonth,
  view,
  next,
  prev,
  goYearMode,
  selectMonth,
  selectYear,
  selectDate,
  value,
  hoverDate,
  onHover,
  locale,
  mode = "range",
  hasTime = false,
  onChange,
  onClose,
  minDate,
  maxDate,
  hasPeriodPicker = false,
  goToPeriodView,
  goToFreeRange,
  selectPeriodMonth,
  selectQuarter,
  isInFreeRange = false,
  highlightedRanges,
}: Props) {
  const t = CALENDAR_LOCALE[locale];

  function getHeaderLabel(): string {
    if (view === "period") {
      const yearStr = `${currentMonth.getFullYear()}`;
      return t.yearSuffix ? `${yearStr} ${t.yearSuffix}` : yearStr;
    }
    if (view === "day") {
      return `${t.months[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    }
    return `${currentMonth.getFullYear()}`;
  }

  const monthLabel = getHeaderLabel();
  const isRange = mode === "range";
  const showBackButton = hasPeriodPicker && view !== "period" && (isInFreeRange || view === "day");

  return (
    <div className="p-2 w-fit">
      {hasTime && mode === "single" && view === "day" ? (
        <div className="flex gap-4">
          <div className="flex-1">
            <CalendarHeader label={monthLabel} onPrev={prev} onNext={next} onClickLabel={goYearMode} />
            <CalendarDays
              month={currentMonth}
              range={value}
              onSelect={selectDate}
              hoverDate={isRange ? hoverDate : null}
              onHover={isRange ? onHover : undefined}
              locale={locale}
              mode={mode}
              minDate={minDate}
              maxDate={maxDate}
              highlightedRanges={highlightedRanges}
            />
          </div>

          <div className="flex flex-col border-l surface-base-stroke pl-4">
            <TimePicker
              value={value.start}
              onChange={(date) => {
                if (onChange) {
                  onChange(date);
                }
              }}
              onDateChange={(date) => {
                if (!value.start && onChange) {
                  onChange(date);
                }
              }}
              locale={locale}
            />
            {onClose && (
              <div className="mt-3">
                <Button type="button" variant="primary" className="w-full" size="md" onClick={onClose}>
                  {t.done}
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {showBackButton && goToPeriodView && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToPeriodView();
              }}
              className="flex items-center gap-1 text-body-regular-sm content-action-brand cursor-pointer mb-1">
              <ArrowLeft2 size={14} color="currentColor" />
              {t.back}
            </button>
          )}

          <CalendarHeader label={monthLabel} onPrev={prev} onNext={next} onClickLabel={goYearMode} />

          {view === "period" && selectPeriodMonth && selectQuarter && goToFreeRange && (
            <CalendarPeriods
              year={currentMonth.getFullYear()}
              value={value}
              onSelectMonth={selectPeriodMonth}
              onSelectQuarter={selectQuarter}
              onFreeRange={goToFreeRange}
              locale={locale}
            />
          )}

          {view === "day" && (
            <CalendarDays
              month={currentMonth}
              range={value}
              onSelect={selectDate}
              hoverDate={isRange ? hoverDate : null}
              onHover={isRange ? onHover : undefined}
              locale={locale}
              mode={mode}
              minDate={minDate}
              maxDate={maxDate}
              highlightedRanges={highlightedRanges}
            />
          )}

          {view === "month" && (
            <CalendarMonths
              currentMonth={currentMonth.getMonth()}
              currentYear={currentMonth.getFullYear()}
              onSelect={selectMonth}
              locale={locale}
            />
          )}

          {view === "year" && <CalendarYears currentYear={currentMonth.getFullYear()} onSelect={selectYear} />}
        </>
      )}
    </div>
  );
}
